import { motion } from "framer-motion";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import { Button } from "@/components/ui/button";

const BULLETS = [
  "Authorized TallyPrime distributor & implementation partner in India",
  "Certified engineers with 20+ years of Tally deployment expertise",
  "Trusted across retail, manufacturing, wholesale and services",
  "End-to-end services — from licensing to migration to AMC",
];

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Left image - ISS template style */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(13,71,161,0.15)] border border-[#0D47A1]/10 group">
            <img
              src="https://images.pexels.com/photos/6779567/pexels-photo-6779567.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt="Team analyzing finances"
              className="w-full h-[560px] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D47A1]/40 via-transparent to-transparent" />

            {/* Floating badge */}
            <div className="absolute top-6 left-6 glass rounded-xl px-4 py-2.5 flex items-center gap-2">
              <div className="w-2 h-2 bg-[#FFA000] rounded-full shadow-[0_0_0_4px_rgba(255,160,0,0.2)]" />
              <span className="text-xs font-semibold text-[#1A1A2E]">Live GST filing</span>
            </div>
          </div>

          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="absolute -bottom-8 -right-6 sm:-right-8 w-64 bg-white rounded-2xl p-5 border border-[#0D47A1]/10 shadow-[0_20px_50px_rgba(13,71,161,0.12)]"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black text-4xl text-[#0D47A1] tracking-tight">20+</span>
              <span className="text-sm text-[#4A5568]">Years</span>
            </div>
            <p className="mt-1 text-xs text-[#4A5568] leading-relaxed">
              Delivering Tally solutions to Indian businesses across 12+ states.
            </p>
          </motion.div>
        </motion.div>

        {/* Right copy - ISS value proposition */}
        <motion.div
          variants={stagger(0.1, 0.09)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> About Us</motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]"
          >
            Essential Tally Solutions{" "}
            <span className="font-serif-accent text-[#FFA000]">For Modern</span>{" "}
            Businesses.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-6 text-[17px] leading-relaxed text-[#4A5568] max-w-xl">
            For over a decade, TallyPrime Solutions has helped small, mid-sized and enterprise
            businesses run smoother books, stay GST-compliant, and unlock growth using
            TallyPrime. From licensing to installation, data migration to training — we own
            your journey end-to-end.
          </motion.p>

          <motion.ul variants={stagger(0.05, 0.06)} className="mt-8 space-y-3">
            {BULLETS.map((b) => (
              <motion.li key={b} variants={fadeUp} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#FFA000] shrink-0" />
                <span className="text-[15px] text-[#1A1A2E]/80">{b}</span>
              </motion.li>
            ))}
          </motion.ul>

          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => scrollTo("#contact")}
              data-testid="about-talk-button"
              className="btn-magnetic rounded-lg bg-[#0D47A1] hover:bg-[#0A3680] text-white h-12 px-6 font-semibold shadow-[0_10px_30px_rgba(13,71,161,0.3)]"
            >
              Talk to an Expert
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              onClick={() => scrollTo("#pricing")}
              data-testid="about-pricing-button"
              variant="outline"
              className="btn-magnetic rounded-lg h-12 px-6 font-semibold border-[#0D47A1]/20 text-[#0D47A1] hover:bg-[#0D47A1]/5"
            >
              See Pricing
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
